import React, { useState } from "react";
import styled from "styled-components";
import { Dropdown } from "..";
import { DropdownStyles } from "../../types/styles";
import { useTranslation } from "react-i18next";
import * as Mock from "../../mocks";

const TimeSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 5px;
  font-size: 13px;
`;

const TimeSelectorLabel = styled.label<{
  styles: DropdownStyles;
}>`
  color: ${({ styles }) => styles.OverallGeneralTextColour};
  font-weight: 700;
  font-size: 13px;
`;

const DropdownContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const ErrorMessage = styled.div<{
  styles: DropdownStyles;
}>`
  color: ${({ styles }) => styles.DropdownGeneralValidationErrorColour};
`;

export type TimeSelectorProps = {
  selectedTimeSlot?: string | null;
  setTimeSlot: React.Dispatch<React.SetStateAction<string | undefined>>;
  styles: DropdownStyles;
  errorMessage?: string;
};

export const TimeSelector: React.FC<TimeSelectorProps> = ({
  selectedTimeSlot,
  setTimeSlot,
  styles,
  errorMessage,
}) => {
  const { t } = useTranslation();

  // State for hour and minutes
  const [hour, setHour] = useState<string>(
    selectedTimeSlot?.split(":")[0] || ""
  );
  const [minutes, setMinutes] = useState<string>(
    selectedTimeSlot?.split(":")[1] || ""
  );

  const handleHourChange = (value: string) => {
    setHour(value);
    updateTimeSlot(value, minutes);
  };

  const handleMinuteChange = (value: string) => {
    setMinutes(value);
    updateTimeSlot(hour, value);
  };

  const updateTimeSlot = (hourValue: string, minutesValue: string) => {
    /*
     Construct time slot string based on hour and minutes values:
     - If both hour and minutes are selected, format as "hour:minutes".
     - If only hour is selected, format as "hour:00".
     - If neither is selected, return an empty string.
   */
    const timeSlot =
      hourValue && minutesValue
        ? `${hourValue}:${minutesValue}`
        : hourValue
          ? `${hourValue}:00`
          : "";

    setTimeSlot(timeSlot);
  };

  return (
    <TimeSelectorContainer data-id="time-selector-container">
      <TimeSelectorLabel styles={styles}>{t("Time")}</TimeSelectorLabel>
      <DropdownContainer>
        <Dropdown
          selected={hour}
          options={Mock.hourOptions}
          onSelect={(selectedHour) => handleHourChange(selectedHour as string)}
          placeholder={t("HH")}
          styles={styles}
          isErrored={!!errorMessage && !hour}
          dataId="time-selector-hour"
          ariaLabel={t("Hour")}
        />
        <Dropdown
          selected={minutes}
          options={Mock.minuteOptions}
          onSelect={(selectedMinutes) =>
            handleMinuteChange(selectedMinutes as string)
          }
          placeholder={t("MM")}
          styles={styles}
          dataId="time-selector-minute"
          ariaLabel={t("Minutes")}
        />
      </DropdownContainer>
      {errorMessage && !selectedTimeSlot && (
        <ErrorMessage styles={styles} data-id="time-selector-error">
          {errorMessage}
        </ErrorMessage>
      )}
    </TimeSelectorContainer>
  );
};

export default TimeSelector;
